import { useQuery } from "@tanstack/react-query";
import { getSubscription } from "@features/billing/api/billing";
import * as React from "react";
import { useAuth } from "@app/providers/auth";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { hasRole, hasPermission } from "@shared/rbac/guards";
import Forbidden from "@app/routes/errors/Forbidden";
import {
  refresh as refreshAuth,
  type RefreshResponse,
} from "@shared/lib/api/auth";
import { AUTH_KEY, type StoredUser } from "@shared/lib/tokenStorage";
import { LoadingState } from "@shared/ui";
import { useTenant } from "@app/providers/tenant";

import {
  createLoginRedirectSearch,
  getMissingTenantRedirect,
} from "./routeGuards";
import { useTranslation } from "react-i18next";
import { useOnline } from "@shared/lib/pwa";

function detectRemember() {
  if (typeof window === "undefined") return false;
  try {
    return Boolean(window.localStorage.getItem(AUTH_KEY));
  } catch {
    return false;
  }
}

function getRefreshExpiry(response: RefreshResponse): number | null {
  if (typeof response.expiresAt === "number") return response.expiresAt;
  return response.expiresIn ? Date.now() + response.expiresIn * 1000 : null;
}

function buildRecoveredSession(
  response: RefreshResponse,
  currentRefreshToken: string,
  currentUser: StoredUser | null,
  tenantId: string | null,
) {
  return {
    accessToken: response.accessToken,
    refreshToken:
      typeof response.refreshToken !== "undefined"
        ? response.refreshToken
        : currentRefreshToken,
    user: response.user ?? currentUser ?? null,
    expiresAt: getRefreshExpiry(response),
    remember: detectRemember(),
    tenantId,
  };
}

export function ProtectedRoute({
  requireRoles,
  requirePerms,
  showTrialBanner = true,
  allowOfflineSession = false,
  children,
}: {
  requireRoles?: string[];
  requirePerms?: string[];
  showTrialBanner?: boolean;
  allowOfflineSession?: boolean;
  children: React.ReactNode;
}) {
  const online = useOnline();
  const { t: tUi } = useTranslation("ui");
  const {
    accessToken,
    getToken,
    refreshToken,
    user,
    login,
    logout,
    tenantId: authTenantId,
  } = useAuth();
  const { tenantId } = useTenant();
  const token = getToken();
  const effectiveTenantId = tenantId ?? authTenantId ?? null;
  const hasOfflineSession = Boolean(
    allowOfflineSession &&
    typeof navigator !== "undefined" &&
    !online &&
    user &&
    effectiveTenantId &&
    (accessToken || refreshToken),
  );
  const hasSessionMaterial = Boolean(
    token || refreshToken || hasOfflineSession,
  );
  const navigate = useNavigate();
  const { location } = useRouterState({
    select: (s) => ({ location: s.location }),
  });
  const [recoveringSession, setRecoveringSession] = React.useState(false);
  const refreshInFlightRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (token || !refreshToken || hasOfflineSession || !online) {
      if (token) refreshInFlightRef.current = null;
      setRecoveringSession(false);
      return;
    }

    if (refreshInFlightRef.current === refreshToken) return;
    refreshInFlightRef.current = refreshToken;

    let cancelled = false;
    setRecoveringSession(true);

    void refreshAuth(refreshToken)
      .then((response) => {
        if (cancelled) return;

        login(
          buildRecoveredSession(
            response,
            refreshToken,
            user,
            effectiveTenantId,
          ),
        );
      })
      .catch(() => {
        if (!cancelled) {
          void logout({ reason: "session-expired", server: false });
        }
      })
      .finally(() => {
        if (!cancelled) {
          refreshInFlightRef.current = null;
          setRecoveringSession(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    effectiveTenantId,
    hasOfflineSession,
    login,
    logout,
    online,
    refreshToken,
    token,
    user,
  ]);

  React.useEffect(() => {
    if (!hasSessionMaterial) {
      if (location.pathname.startsWith("/login")) return;
      navigate({
        to: "/login",
        search: createLoginRedirectSearch(location, "auth-required") as any,
        replace: true,
      } as any);
    }
  }, [hasSessionMaterial, navigate, location]);

  React.useEffect(() => {
    if (!token || recoveringSession) return;
    if (effectiveTenantId) return;

    const redirectTo = getMissingTenantRedirect(location.pathname);
    if (!redirectTo) return;

    navigate({ to: redirectTo, replace: true } as any);
  }, [
    effectiveTenantId,
    location.pathname,
    navigate,
    recoveringSession,
    token,
  ]);

  const billingSubscription = useQuery({
    queryKey: ["billing", effectiveTenantId || "", "subscription"],
    queryFn: () => getSubscription(effectiveTenantId || undefined),
    enabled:
      !!token && !!effectiveTenantId && hasPermission(user, "billing:read"),
    retry: false,
  });
  const subStatus = billingSubscription.data?.status
    ?.toLowerCase()
    .replace(/[\s_-]/g, "");
  React.useEffect(() => {
    if (
      subStatus === "pastdue" &&
      !location.pathname.startsWith("/billing") &&
      !location.pathname.startsWith("/settings/account")
    )
      navigate({ to: "/billing" });
  }, [subStatus, location.pathname, navigate]);

  if (!hasSessionMaterial) {
    return (
      <LoadingState
        label={tUi("redirectingToSignIn", {
          defaultValue: "Redirecting to sign in...",
        })}
      />
    );
  }

  if (!token && refreshToken && !hasOfflineSession) {
    return (
      <LoadingState
        label={tUi("restoringYourAdminSession", {
          defaultValue: "Restoring your admin session...",
        })}
      />
    );
  }

  if (!effectiveTenantId && getMissingTenantRedirect(location.pathname)) {
    return (
      <LoadingState
        label={tUi("restoringStoreContext", {
          defaultValue: "Restoring store context...",
        })}
      />
    );
  }

  //  Always allow admins
  const isAdmin =
    user?.roles?.includes("Admin") || user?.roles?.includes("TenantAdmin");
  if (isAdmin) return <>{children}</>;

  // --- Role-based restriction ---
  if (requireRoles && !hasRole(user as any, requireRoles)) {
    return <Forbidden />;
  }

  // --- Permission-based restriction ---
  if (
    requirePerms &&
    requirePerms.some((p) => !hasPermission(user as any, p))
  ) {
    return <Forbidden />;
  }

  // If we know it's expired and not on billing, block render (avoids flash)
  if (
    subStatus === "pastdue" &&
    !location.pathname.includes("/billing") &&
    !location.pathname.includes("/settings/account")
  ) {
    return null;
  }

  return (
    <>
      {showTrialBanner ? (
        <React.Suspense fallback={null}>
          <TrialBannerWrapper />
        </React.Suspense>
      ) : null}
      {children}
    </>
  );
}

// Lazy load banner to avoid circular deps if any
const TrialBannerWrapper = React.lazy(() =>
  import("@widgets/layout/TrialBanner").then((m) => ({
    default: m.TrialBanner,
  })),
);

export default ProtectedRoute;
